import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'Ståltag',
    besked: '',
    website_url: '' // Honeypot field
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    // Check honeypot
    if (formData.website_url) {
      // Spam bot detected, silently pretend success
      setSubmitting(false);
      setSubmitStatus('success');
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          besked: formData.besked,
          website_url: formData.website_url
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: 'Ståltag',
          besked: '',
          website_url: ''
        });
      } else {
        const data = await response.json().catch(() => ({}));
        setSubmitStatus('error');
        setErrorMessage(data.message || 'Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring direkte til os.');
      }
    } catch (err) {
      console.error('Webform error:', err);
      setSubmitStatus('error');
      setErrorMessage('Der kunne ikke oprettes forbindelse. Tjek din internetforbindelse eller ring direkte på 28 59 43 49.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      <Helmet>
        <title>Kontakt · TH Byg v/Jacob Hartvig Hansen</title>
        <meta name="description" content="Du er altid velkommen til at kontakte T-H Byg på telefon 28 59 43 49 for et uforpligtende tilbud, eller hvis du vil have mig med på råd." />
        <meta property="og:title" content="Kontakt · TH Byg v/Jacob Hartvig Hansen" />
        <meta property="og:description" content="Du er altid velkommen til at kontakte T-H Byg på telefon 28 59 43 49 for et uforpligtende tilbud, eller hvis du vil have mig med på råd." />
      </Helmet>

      {/* Hero Header */}
      <section className="bg-[#11161a] text-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-black uppercase tracking-widest text-[#d92323]">
              Uforpligtende Tilbud
            </span>
            <h1 className="text-3xl sm:text-5xl font-black text-white mt-2 tracking-tight">
              Kontakt os i dag
            </h1>
            <p className="mt-4 text-slate-300 text-base sm:text-lg leading-relaxed">
              Du er altid velkommen til at kontakte T-H Byg på telefon 28 59 43 49 for et uforpligtende tilbud, eller hvis du vil have mig med på råd.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Grid */}
      <section className="py-16 sm:py-24 bg-white text-slate-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Info Column */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-4">
                  Virksomhedsoplysninger
                </h2>
                <div className="space-y-6 text-slate-700">
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-[#f4f5f7] border border-slate-200">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#d92323] text-white">
                      <i className="fa-solid fa-building" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-base">T.H Byg</h3>
                      <p className="text-sm text-slate-600">v/ Jacob Hartvig Hansen</p>
                      <p className="text-xs text-slate-500 mt-1">CVR-nummer: 31819547</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 rounded-xl bg-[#f4f5f7] border border-slate-200">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#d92323] text-white">
                      <i className="fa-solid fa-location-dot" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-base">Adresse</h3>
                      <p className="text-sm text-slate-600">Skovhusvej 23</p>
                      <p className="text-sm text-slate-600">8763 Rask Mølle</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 rounded-xl bg-[#f4f5f7] border border-slate-200">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#d92323] text-white">
                      <i className="fa-solid fa-phone" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-base">Telefon</h3>
                      <a href="tel:28594349" className="text-base font-extrabold text-[#d92323] hover:underline whitespace-nowrap">
                        (+45) 28 59 43 49
                      </a>
                      <p className="text-xs text-slate-500 mt-0.5">Svarer hurtigst muligt</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 rounded-xl bg-[#f4f5f7] border border-slate-200">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#d92323] text-white">
                      <i className="fa-solid fa-envelope" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-base">E-mail</h3>
                      <a href="mailto:jacob.t.h.byg@gmail.com" className="text-sm font-semibold text-slate-800 hover:text-[#d92323] hover:underline whitespace-nowrap">
                        jacob.t.h.byg@gmail.com
                      </a>
                      <p className="text-xs text-slate-500 mt-0.5">Send besked direkte til indbakken</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map embed / location card */}
              <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200">
                <iframe
                  title="T.H Byg placering Rask Mølle"
                  src="https://maps.google.com/maps?q=Skovhusvej%2023%2C%208763%20Rask%20M%C3%B8lle&t=&z=13&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="260"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                />
              </div>
            </div>

            {/* Form Column */}
            <div className="lg:col-span-7">
              <div className="bg-[#11161a] text-white p-8 sm:p-10 rounded-2xl shadow-2xl border border-slate-800">
                <h2 className="text-2xl font-extrabold text-white mb-2">
                  Send en besked
                </h2>
                <p className="text-sm text-slate-300 mb-8">
                  Udfyld formularen, så vender Jacob tilbage hurtigst muligt – normalt inden for 24 timer.
                </p>

                {submitStatus === 'success' && (
                  <div className="mb-6 p-6 rounded-xl bg-emerald-950/80 border border-emerald-500/40 text-emerald-200 flex items-start gap-4">
                    <i className="fa-solid fa-circle-check text-emerald-400 text-2xl mt-0.5" />
                    <div>
                      <h3 className="font-bold text-white text-base">Tak for din henvendelse!</h3>
                      <p className="text-sm mt-1">Vi har modtaget din besked og vender tilbage hurtigst muligt.</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-6 rounded-xl bg-red-950/80 border border-red-500/40 text-red-200 flex items-start gap-4">
                    <i className="fa-solid fa-triangle-exclamation text-red-400 text-2xl mt-0.5" />
                    <div>
                      <h3 className="font-bold text-white text-base">Kunne ikke sende besked</h3>
                      <p className="text-sm mt-1">{errorMessage}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Mandatory Honeypot field for Antispam */}
                  <div className="sr-only absolute -left-[9999px]">
                    <label htmlFor="website_url">Hjemmeside url (udfyld ikke)</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      tabIndex="-1"
                      autoComplete="off"
                      value={formData.website_url}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1.5">
                      Navn <span className="text-[#d92323]">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Dit fulde navn"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full rounded-xl bg-slate-900 border border-slate-700 px-4 py-3.5 text-white placeholder-slate-500 focus:border-[#d92323] focus:outline-none focus:ring-2 focus:ring-[#d92323]/20 transition-all text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1.5">
                        Telefonnummer <span className="text-[#d92323]">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="f.eks. 28 59 43 49"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full rounded-xl bg-slate-900 border border-slate-700 px-4 py-3.5 text-white placeholder-slate-500 focus:border-[#d92323] focus:outline-none focus:ring-2 focus:ring-[#d92323]/20 transition-all text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1.5">
                        E-mail <span className="text-[#d92323]">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="din@email.dk"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full rounded-xl bg-slate-900 border border-slate-700 px-4 py-3.5 text-white placeholder-slate-500 focus:border-[#d92323] focus:outline-none focus:ring-2 focus:ring-[#d92323]/20 transition-all text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1.5">
                      Hvad drejer opgaven sig om?
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full rounded-xl bg-slate-900 border border-slate-700 px-4 py-3.5 text-white focus:border-[#d92323] focus:outline-none focus:ring-2 focus:ring-[#d92323]/20 transition-all text-sm"
                    >
                      <option value="Ståltag">Ståltag (Specialer)</option>
                      <option value="Metrotile og Decra tag">Metrotile og Decra tag</option>
                      <option value="Tagrenovering">Tagrenovering / Nyt tag</option>
                      <option value="Døre og vinduer">Døre og vinduer</option>
                      <option value="Tilbygning og ombygning">Tilbygning og ombygning</option>
                      <option value="Totalrenovering">Totalrenovering</option>
                      <option value="Andet tømrerarbejde">Andet tømrerarbejde</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1.5">
                      Besked / Projektbeskrivelse <span className="text-[#d92323]">*</span>
                    </label>
                    <textarea
                      name="besked"
                      required
                      rows="5"
                      placeholder="Beskriv dit projekt, adresse eller ønsker..."
                      value={formData.besked}
                      onChange={handleChange}
                      className="w-full rounded-xl bg-slate-900 border border-slate-700 px-4 py-3.5 text-white placeholder-slate-500 focus:border-[#d92323] focus:outline-none focus:ring-2 focus:ring-[#d92323]/20 transition-all text-sm"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#d92323] py-4 text-base font-extrabold text-white shadow-xl hover:bg-[#b81b1b] transition-all disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin" />
                        <span>Sender besked...</span>
                      </>
                    ) : (
                      <>
                        <span>Send besked.</span>
                        <i className="fa-solid fa-paper-plane text-sm" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
